import * as React from "react";
import { useState } from "react";
import * as Sentry from "@sentry/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Item } from "@/types/fse";
import { useRemoveFileMutation } from "@/api/files";
import { toast } from "react-toastify";
import * as analytics from "@/services/analytics";

interface Props {
  item: Item;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  onRemove?: () => void;
}

export const DeleteModal: React.FC<Props> = ({ isOpen, setIsOpen, item, onRemove }) => {
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [removeFile] = useRemoveFileMutation();

  const handleRemove = async () => {
    setIsProcessing(true);
    setError("");

    try {
      await removeFile(item.id).unwrap();
      if (item.type === "folder") {
        analytics.folderRemove(item.id, item.name);
      } else {
        analytics.documentRemove(item.id, item.name);
      }
      setIsOpen(false);
      toast.success(`${item.name} has been removed successfully.`);
      onRemove?.();
    } catch (error) {
      setError("Failed to remove the item. Please try again.");
      Sentry.captureException(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove Item</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove {item.name}? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-red-500">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleRemove} disabled={isProcessing}>
            {isProcessing ? "Removing..." : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
